import React, { useEffect, useState } from 'react';
import { Tabs, Tab, Box } from '@mui/material';
import PropTypes from 'prop-types';
import { useLocation } from 'react-router-dom';

/**
 * Reusable Tabs Component
 * @param {Array} tabs - Array of tab objects with { label, icon, content }
 * @param {string} variant - MUI Tab variant ('standard', 'scrollable', 'fullWidth')
 * @param {string} orientation - Tab orientation ('horizontal', 'vertical')
 * @param {boolean} allowScrollButtons - Show scroll buttons for scrollable tabs
 * @param {string} textColor - Tab text color ('primary', 'secondary', 'inherit')
 * @param {string} indicatorColor - Tab indicator color ('primary', 'secondary')
 * @param {string} className - Additional CSS classes
 * @param {function} onChange - Callback when tab changes (tabIndex, tabData)
 * @param {number} defaultTab - Default active tab index
 */
const ReusableTabs = ({
  tabs = [],
  variant = 'standard',
  orientation = 'horizontal',
  allowScrollButtons = false,
  textColor = 'primary',
  indicatorColor = 'primary',
  className = '',
  onChange,
  defaultTab = 0,
  ...props
}) => {
  const [activeTab, setActiveTab] = useState(defaultTab);
  const location = useLocation(0);
  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
    if (onChange) {
      onChange(newValue, tabs[newValue]);
    }
  };
  useEffect(() => {
    setActiveTab(location?.state?.defaultValue ?? defaultTab);
  }, [location]);
  return (
    <Box className={`${className}`} {...props}>
      <Tabs
        value={activeTab}
        onChange={handleTabChange}
        variant={variant}
        orientation={orientation}
        scrollButtons={allowScrollButtons ? 'auto' : false}
        allowScrollButtonsMobile={allowScrollButtons}
        textColor={textColor}
        indicatorColor={indicatorColor}
        TabScrollButtonProps={{
          sx: {
            flexShrink: 0, // ensures scroll buttons don't collapse
          },
        }}
        sx={{
          '.MuiTabs-flexContainer': {
            gap: orientation === 'horizontal' ? '8px' : '4px',
          },
          ...(variant === 'fullWidth' && {
            '.MuiTab-root': {
              flex: 1, // stretch each tab equally
            },
          }),
        }}
      >
        {tabs.map((tab, index) => (
          <Tab
            key={index}
            label={tab.label}
            icon={tab.icon}
            iconPosition={tab.iconPosition || 'start'}
            disabled={tab.disabled || false}
            sx={{
              minHeight: 48,
              textTransform: 'none',
              fontSize: '16px',
              fontWeight: 500,
              ...(variant === 'fullWidth' && { flex: 1 }),
              ...tab.sx,
            }}
          />
        ))}
      </Tabs>

      {/* Tab Panels */}
      {tabs.map((tab, index) => (
        <TabPanel key={index} value={activeTab} index={index}>
          {tab.content}
        </TabPanel>
      ))}
    </Box>
  );
};

/**
 * Tab Panel Component
 */
const TabPanel = ({ children, value, index, ...other }) => {
  return (
    <div
      role='tabpanel'
      hidden={value !== index}
      id={`tabpanel-${index}`}
      aria-labelledby={`tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 2 }}>{children}</Box>}
    </div>
  );
};

TabPanel.propTypes = {
  children: PropTypes.node,
  value: PropTypes.number.isRequired,
  index: PropTypes.number.isRequired,
};

ReusableTabs.propTypes = {
  tabs: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      icon: PropTypes.element,
      iconPosition: PropTypes.oneOf(['start', 'end', 'top', 'bottom']),
      content: PropTypes.node.isRequired,
      disabled: PropTypes.bool,
      sx: PropTypes.object,
    }),
  ).isRequired,
  variant: PropTypes.oneOf(['standard', 'scrollable', 'fullWidth']),
  orientation: PropTypes.oneOf(['horizontal', 'vertical']),
  allowScrollButtons: PropTypes.bool,
  textColor: PropTypes.oneOf(['primary', 'secondary', 'inherit']),
  indicatorColor: PropTypes.oneOf(['primary', 'secondary']),
  className: PropTypes.string,
  onChange: PropTypes.func,
  defaultTab: PropTypes.number,
};

export default ReusableTabs;
